import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined';
import SettingsIcon from '@material-ui/icons/SettingsOutlined';
import NamedBox from '../atoms/NamedBox';
import ProgressLine from '../atoms/ProgressLine';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import {
  COLOR_BACKGROUND_WARM,
  COLOR_BORDER,
  COLOR_MIDDLE_GRAY,
  COLOR_ORANGE,
  COLOR_TEXT,
  COLOR_WHITE,
} from '../styles/colors';
import { camelCaseToTitleCase } from '../services/converter';

interface PlayerValuationsProps {
  className?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    border: `1px solid ${COLOR_BACKGROUND_WARM}`,
  },
  headerBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    background: COLOR_BACKGROUND_WARM,
    borderBottom: `1px solid ${COLOR_BORDER}`,
    fontFamily: FONT_PROXIMA_NOVA,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    fontWeight: 700,
  },
  tfvBadge: {
    height: 22,
    padding: theme.spacing(0.25, 1),
    borderRadius: 4,
    background: COLOR_ORANGE,
    color: COLOR_WHITE,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerRight: {
    minWidth: 100,
  },
  select: {
    fontSize: theme.typography.pxToRem(12),
  },
  summaryRow: {
    borderBottom: `1px solid ${COLOR_BORDER}`,
    padding: theme.spacing(2, 2),
    background: COLOR_WHITE,
  },
  summaryRowInner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  summaryBlock: {
    flex: '0 0 24%',
    minWidth: 0,
  },
  summaryIconsCell: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  summaryValueCell: {
    flex: '0 0 120px',
    textAlign: 'right',
  },
  summaryTitle: {
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  summarySubtitle: {
    fontSize: theme.typography.pxToRem(11),
    color: COLOR_MIDDLE_GRAY,
    marginBottom: theme.spacing(1),
  },
  summaryValueRow: {
    display: 'flex',
    flexDirection: 'column',
  },
  eyeIcons: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: COLOR_MIDDLE_GRAY,
  },
  summaryValue: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 700,
    color: COLOR_ORANGE,
  },
  miniScore: {
    fontWeight: 700,
    marginRight: theme.spacing(1),
  },
  miniLogo: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    background: COLOR_ORANGE,
    marginLeft: theme.spacing(1),
  },
  factorsHeader: {
    padding: theme.spacing(1.5, 2),
    background: COLOR_BACKGROUND_WARM,
    borderBottom: `1px solid ${COLOR_BORDER}`,
    fontFamily: FONT_PROXIMA_NOVA,
    fontWeight: 700,
  },
  factorsRow: {
    padding: theme.spacing(1, 2, 2),
  },
  factorGrid: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  factorCol: {
    [theme.breakpoints.up('md')]: {
      borderRight: `1px solid ${COLOR_BORDER}`,
      '&:last-child': {
        borderRight: 'none',
      },
    },
    [theme.breakpoints.down('sm')]: {
      borderRight: 'none',
      borderBottom: `1px solid ${COLOR_BORDER}`,
      '&:last-child': {
        borderBottom: 'none',
      },
      paddingBottom: theme.spacing(1.5),
      marginBottom: theme.spacing(1.5),
    },
    paddingRight: theme.spacing(2),
  },
  factorRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1.25),
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
  },
  factorName: {
    width: 70,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
  },
  factorInfoIcon: {
    fontSize: 14,
    color: COLOR_MIDDLE_GRAY,
    marginRight: theme.spacing(1),
  },
  factorProgress: {
    flex: 1,
    marginRight: theme.spacing(1),
  },
  factorScore: {
    width: 30,
    textAlign: 'right',
    fontSize: theme.typography.pxToRem(16),
  },
  progressBar:{
    background: COLOR_ORANGE,
  }
}), { name: 'PlayerValuations' });

const FACTOR_COLUMNS: Array<Array<{ key: string; label: string }>> = [
  [
    { key: 'ppi', label: 'PPI' },
    { key: 'ppiRec', label: 'PPI Rec.' },
    { key: 'pff', label: 'PFF' },
    { key: 'pffRec', label: 'PFF Rec.' },
    { key: 'pai', label: 'PAI' },
    { key: 'speedPercentile', label: 'Spd %' },
    { key: 'powerPercentile', label: 'Pwr %' },
    { key: 'ms', label: 'MS' },
  ],
  [
    { key: 'heightPercentile', label: 'H %' },
    { key: 'weightPercentile', label: 'W %' },
    { key: 'expLvl', label: 'Exp. Lvl' },
    { key: 'gmExp', label: 'Gm Exp.' },
    { key: '247', label: '247' },
    { key: 'portal', label: 'Portal' },
    { key: 'eligibility', label: 'Elig.' },
    { key: 'tenure', label: 'Tenure' },
  ],
  [
    { key: 'allAmerican', label: 'All-Amer.' },
    { key: 'allConference', label: 'All-Conf.' },
    { key: 'offerCount', label: 'Offer #' },
    { key: 'offerQuality', label: 'Offer Qual.' },
    { key: 'on3PublicNILValuation', label: 'On3 Val.' },
    { key: 'on3RosterValue', label: 'On3 RV' },
    { key: 'on3PrivateNILValue', label: 'On3 NV' },
    { key: 'commitStr', label: 'Commit Str.' },
    { key: 'hsAvail', label: 'HS Avail.' },
  ],
];

const mockApiResponse = {
  playerIds: 10000,
  teamId: 124,
  positionCode: 'QB',
  teamValuation: {
    valuation: 244330,
    score: 3.9,
  },
  tfCollegeValuation: {
    valuation: 244330,
    score: 3.9,
  },
  playerFactors: {
    ppi: 89,
    ppiRec: 68,
    pff: 98,
    pffRec: 93,
    pai: 0,
    speedPercentile: 0,
    powerPercentile: 0,
    ms: 100,
    heightPercentile: 39,
    weightPercentile: 57,
    expLvl: 100,
    gmExp: 100,
    247: 96,
    portal: 0,
    eligibility: 20,
    tenure: 100,
    allAmerican: 0,
    allConference: 0,
    offerCount: 75,
    offerQuality: 100,
    on3PublicNILValuation: 100,
    on3RosterValue: 100,
    on3PrivateNILValue: 100,
    commitStr: null,
    hsAvail: null,
  },
};

export default function PlayerValuations(props: PlayerValuationsProps) {
  const { className } = props;
  const classes = useStyles();

  const data = mockApiResponse; // Using mock API response

  return (
    <div className={clsx(classes.root, className)}>
      <div className={classes.headerBar}>
        <div className={classes.headerLeft}>
          <div className={classes.tfvBadge}>TFV</div>
          <span>Active Valuations</span>
        </div>
        <Select
          className={classes.headerRight}
          value={data.positionCode}
          inputProps={{ className: classes.select }}
          disableUnderline
        >
          <MenuItem value={data.positionCode}>{data.positionCode}</MenuItem>
        </Select>
      </div>

      <div className={classes.summaryRow}>
        <div className={classes.summaryRowInner}>
          {/* Team Valuation */}
          <div className={classes.summaryBlock}>
            <div className={classes.summaryTitle}>Team Valuation</div>
            <div className={classes.summarySubtitle}>Confidential Internal Valuation</div>
          </div>

          <div className={classes.summaryIconsCell}>
            <div className={classes.eyeIcons}>
              <SettingsIcon fontSize="small" />
              <VisibilityIcon fontSize="small" />
            </div>
          </div>

          <div className={classes.summaryValueCell}>
            <div className={classes.summaryValue}>${data.teamValuation.valuation.toLocaleString()}</div>
          </div>

          {/* TF College Valuation */}
          <div className={classes.summaryBlock}>
            <div className={classes.summaryTitle}>TF College Valuation</div>
            <div className={classes.summarySubtitle}>Tracking Football College TFVS</div>
          </div>

          <div className={classes.summaryIconsCell}>
            <div className={classes.eyeIcons}>
              <SettingsIcon fontSize="small" />
              <VisibilityIcon fontSize="small" />
            </div>
          </div>

          <div className={classes.summaryValueCell}>
            <div className={classes.summaryValue}>${data.tfCollegeValuation.valuation.toLocaleString()}</div>
          </div>
          <div className={classes.summaryIconsCell}>
            <span className={classes.miniScore}>{data.tfCollegeValuation.score.toFixed(1)}</span>
            <div className={classes.miniLogo} />
          </div>
        </div>
      </div>

      <div className={classes.factorsHeader}>Player Factors</div>

      <div className={classes.factorsRow}>
        <Grid container spacing={2} className={classes.factorGrid}>
          {FACTOR_COLUMNS.map((column, columnIndex) => (
            <Grid item xs={12} md={4} key={columnIndex} className={classes.factorCol}>
              {column.map(field => {
                const rawValue = (data.playerFactors as any)[field.key];
                const numericValue = typeof rawValue === 'number' ? rawValue : 0;
                const displayValue = rawValue === null || rawValue === undefined ? '' : rawValue;

                return (
                  <div className={classes.factorRow} key={field.key}>
                    <span className={classes.factorName}>{field.label}</span>
                    <InfoIcon className={classes.factorInfoIcon} />
                    <div className={classes.factorProgress}>
                      <ProgressLine showPercentileSign={false} value={numericValue} progressBarClassName={classes.progressBar} />
                    </div>
                    {/* <span className={classes.factorScore}>{displayValue}</span> */}
                  </div>
                );
              })}
            </Grid>
          ))}
        </Grid>
      </div>
    </div>
  );
}

